import memoize from 'lodash-es/memoize';
import { concat, defer, EMPTY, identity, noop, Observable, Subject } from 'rxjs';
import { htmlFromDom } from '../../helpers/dom/htmlFromDom';
import { fromMutations, MutationFrame } from '../../helpers/fromMutations';
import { sanitizeHtml, SanitizeHtmlOptions } from '../../helpers/sanitizeHtml';
import { removeSelectionBookmarksInElement } from '../../helpers/selectionBookmarks';
import { createConnectingService } from '../base/connectingService';
import { ElementService } from '../dom/elementService';
import { HtmlService, MemoryHtmlService } from './htmlService';

export type ElementHtmlServiceOptions = {
  sanitizeHtmlOptions?: SanitizeHtmlOptions;
  /** Perform initial processing when the element is initialized. */
  processElement?: (element: HTMLElement) => void;
  /** Perform incremental processing after the element's DOM was mutated. */
  performCascadingMutations?: (element: HTMLElement, frame: MutationFrame) => void;
  /** Filter DOM mutation records to reduce processing and change notifications. */
  filterMutationRecord?: (element: HTMLElement, record: MutationRecord) => boolean;
  /** Replacement for legacy simplifyStyles behavior. */
  didSetHtml?: (element: HTMLElement, htmlLength: number) => void;
};

export function createElementHtmlService(
  element$: Observable<HTMLElement | null>,
  options: ElementHtmlServiceOptions = {},
): HtmlService {
  return createConnectingService<HTMLElement, ProcessedHtmlService>(
    element$,
    () => new DetachedElementHtmlService(options),
    element => new ElementHtmlService(element, options),
    (nextService, prevService) => {
      nextService.setCleanHtml(prevService.unprocessedCleanHtml);
    },
  );
}

interface ProcessedHtmlService extends HtmlService {
  readonly unprocessedCleanHtml: string;
}

export class ElementHtmlService extends ElementService<HTMLElement> implements ProcessedHtmlService {
  constructor(element: HTMLElement, options: ElementHtmlServiceOptions = {}) {
    const {
      sanitizeHtmlOptions,
      processElement,
      performCascadingMutations,
      filterMutationRecord,
      didSetHtml,
    } = options;
    // Completes to signal that initial processing was finished.
    const initialProcessingProgress$ = new Subject<void>();

    // Do this after switch effects ran (setCleanHtml in the createConnectingService call above) and before we
    // start observing DOM mutations.
    const initialProcessingEffect$ = defer(() => {
      const didPerformInitialProcessing = initialProcessingProgress$.isStopped;
      if (!didPerformInitialProcessing && processElement) {
        try {
          processElement(element);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Caught error while processing element on init:', error);
        }
      }

      initialProcessingProgress$.complete();
      return EMPTY;
    });

    const mutationProcessingEffects$ = fromMutations(element, {
      performCascadingMutations: performCascadingMutations
        ? frame => performCascadingMutations(element, frame)
        : undefined,
      filterMutationRecord: record => {
        // Ignore attribute changes on the root element, since they don't affect innerHTML.
        if (record.type === 'attributes' && record.target === element) {
          return false;
        }

        return filterMutationRecord ? filterMutationRecord(element, record) : true;
      },
    });

    super(element, {
      changes$: concat(initialProcessingProgress$, mutationProcessingEffects$),
      effects$: concat(initialProcessingEffect$, mutationProcessingEffects$),
    });

    this.sanitizeHtml = dirtyHtml => sanitizeHtml(dirtyHtml, sanitizeHtmlOptions);
    this.didSetHtml = didSetHtml || noop;
  }

  private readonly sanitizeHtml: (html: string) => string;
  private readonly didSetHtml: (element: HTMLElement, htmlLength: number) => void;

  get cleanHtml() {
    const { element } = this;

    // TODO: Should this be here?
    removeSelectionBookmarksInElement(element);
    return element.innerHTML;
  }

  /** @deprecated */
  get html() {
    return this.cleanHtml;
  }

  get unprocessedCleanHtml() {
    // The element is processed ongoingly.
    return this.cleanHtml;
  }

  setDirtyHtml(html: string) {
    this.setCleanHtml(this.sanitizeHtml(html));
  }

  setCleanHtml(html: string) {
    this.element.innerHTML = html;
    this.didSetHtml(this.element, html.length);
  }

  // eslint-disable-next-line class-methods-use-this
  waitForChanges(onDone: () => void) {
    // Changes are processed in the MutationObserver callback, which is called on the microtask queue.
    Promise.resolve().then(() => {
      onDone();
    });
  }
}

export class DetachedElementHtmlService extends MemoryHtmlService implements ProcessedHtmlService {
  constructor(options: ElementHtmlServiceOptions = {}) {
    super(options);

    const { processElement } = options;
    this.processCleanHtml = processElement
      ? memoize(cleanHtml =>
          htmlFromDom((_document, element) => {
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = cleanHtml;
            processElement(element);
          }),
        )
      : identity;
  }

  private readonly processCleanHtml: (cleanHtml: string) => string;

  get cleanHtml() {
    return this.processCleanHtml(this.unprocessedCleanHtml);
  }

  /** @deprecated */
  get html() {
    return this.cleanHtml;
  }

  /** Sanitized, but potentially denormalized HTML. */
  get unprocessedCleanHtml() {
    return super.cleanHtml;
  }
}
